export enum snackbarColor {
    success = '#35C075',
    error = '#DA2C55',
    warning = '#ee5b5b',
    info = '#272530',
}
/** Role codes, as the backend's `Role` model names them (#1836, branch
 *  `new_roles`). Company roles live in `users_roles`, project roles in
 *  `users_projects`. `main_scriptwriter`, `scriptwriter` and `reviewer` are
 *  retired (soft-deleted rows) — kept so old memberships still read.
 *  Superadmin is a user *type* on the backend, not a role. */
export enum userRole {
    company_owner = 'company_owner',
    company_admin = 'company_admin',
    company_employee = 'company_employee',
    project_owner = 'project_owner',
    project_admin = 'project_admin',
    main_scriptwriter = 'main_scriptwriter',
    scriptwriter = 'scriptwriter',
    editor = 'editor',
    reviewer = 'reviewer',
}
export enum gender {
    male = 'male',
    female = 'female',
    other = 'other',
}
export enum assetCategory {
    document = 'document',
    image = 'image',
    background = 'background',
    video = 'video',
    feedback = 'feedback',
    // Frozen contract (characters redesign): dream-cast photo collection.
    // Mock-phase uploads still send 'image'; membership lives in
    // characterExtras until the backend knows this value.
    dreamcast = 'dreamcast',
    // Frozen contract (2026-08-27 design update): «Референс» and «Костюмы и
    // грим» gallery collections, same mock arrangement as dreamcast.
    reference = 'reference',
    costume = 'costume',
}
export enum Status {
    created = 'created',
    negotiation = 'negotiation',
    approved = 'approved',
}
export enum CommentableModel {
    character = 'character',
    project = 'project',
    text_atom = 'text_atom',
    act = 'act',
    episode = 'episode',
    scene = 'scene',
    note = 'note',
    question = 'question',
    storyline = 'storyline',
}
export enum TextableType {
    project = 'project',
    character = 'character',
}
export enum CommentableFieldProject {
    project = 'project',
    audience = 'audience',
    budget_category = 'budget_category',
    title = 'title',
    genre = 'genre',
    episodes_qty = 'episodes_qty',
}
export enum CommentableFieldTextAtom {
    content = 'content',
}
export enum CommentableFieldCharacter {
    name = 'name',
    age_category = 'age_category',
}
export enum CommentableFieldFeature {
    name = 'name',
}
export enum CommentableId {
    logline = 9,
}
export enum CommentableStatus {
    active = 'active',
    resolved = 'resolved',
}
export enum QuestionStatus {
    open = 'open',
    answered = 'answered',
    closed = 'closed',
}
export enum tagCategoryId {
    simpleTag = 1,
    objectTag = 2,
    barriersTag = 3,
    materialObjectTag = 4,
    locationTag = 5,
    characterTag = 6,
    lineTag = 7,
    timeTag = 8,
    interiorTag = 9,
    tbdTag = 10,
}
export enum textAtomTypeId {
    project_synopsis = 1,
    project_logline = 25,
    character_reference = 7,
    line_logline = 8,
    episode_logline = 9,
    feature_description = 10,
    sequence_logline = 11,
    scene_logline = 12,
    scene_location = 13,
    scene_dialogue = 14,
    scene_action = 32,
    scene_remark = 18,
    scene_pep = 33,
    character_relation = 15,
    character_biography = 16,
    intersection_logline = 17,
    episode_arch = 19,
    scene_description = 21,
    episode_act_logline = 26,
    episode_act_tbd = 27,
    character_target = 28,
    character_conflict = 29,
    character_status = 3,
    short_characteristic = 30,
    full_characteristic = 31,
    act_plot = 34,
    episode_plot = 35,
    scene_synopsis = 36,
    act_logline = 37,
    fragment_logline = 38,
    storyline_description = 39,
}
export enum featureTypeId {
    object = 1,
    obstacle = 2,
    materialObject = 3,
    location = 4,
}
export enum Time {
    day = 1,
    night = 2,
}
export enum Interior {
    nat = 3,
    int = 4,
    intExt = 5,
}
export enum CommentNotification {
    CommentRepliedNotification = 'App\\Notifications\\CommentRepliedNotification',
    AssignedToProjectNotification = 'App\\Notifications\\AssignedToProjectNotification',
}
export enum SignCharacter {
    antagonist = 'antagonist',
    secondary = 'secondary',
    protagonist = 'protagonist',
    ally = 'ally',
    hiddenAntagonist = 'hidden_antagonist',
    allyAntagonist = 'ally_antagonist',
    neutral = 'neutral',
}
// Prominence of a character in the story, orthogonal to SignCharacter (the
// role). Drives the four list-page groups; null/absent falls back to the
// legacy derivation from the role (secondary → secondary, else main).
export enum CharacterImportance {
    main = 'main',
    secondary = 'secondary',
    episodic = 'episodic',
    extra = 'extra',
}
// «Тип связи» of a character relationship (#1844). Nullable on the API —
// relations written before the map existed carry none.
export enum RelationType {
    family = 'family',
    romance = 'romance',
    conflict = 'conflict',
    friendship = 'friendship',
}
export enum CharacteristicCode {
    mask = 'mask',
    superpower = 'superpower',
    backside = 'backside',
    fear = 'fear',
    excitement = 'excitement',
    shadow = 'shadow',
    custom = 'custom',
}
export enum Disposition {
    nat = 'nat',
    int = 'int',
}
export enum FileExtension {
    pdf = 'pdf',
    doc = 'msword',
    html = 'html',
}
export enum Mode {
    dark = 'dark',
    light = 'light',
}

export enum SceneEditorButtons {
    formatting = 'formatting',
    comments = 'comments',
    bits = 'bits',
}

export enum Fonts {
    courier = 'Courier New',
    roboto = 'Roboto Flex',
}

export enum episodeImport {
    new = 'new',
    current = 'current',
}

export enum episodeImportLoader {
    checking = 'checking',
    importing = 'importing',
    done = 'done',
}

export enum feedbackType {
    bug = 'bug',
    question = 'question',
    idea = 'idea',
    technical = 'technical',
}

export enum hintsBlock {
    menu = 'menu',
    head = 'head',
    navigator = 'navigator',
    editor = 'editor',
    tools = 'tools',
}

export type ActsType = {
    value: number;
    typeId: textAtomTypeId;
    id: string;
};

export enum ProjectTypeEnum {
    MOVIE = 'movie',
    SERIES = 'series',
}
